import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { pageAnimation } from '@const/animation';
import { routes } from '@const/routes';
import { Icon, TIconType } from '@components/icons';
import Typography from '@components/common/typography';
import Button from '@components/common/button';
import Skeleton from '@components/common/skeleton';
import { apiApiKeysService, apiWebhookService, apiHomeSummaryService } from '@api';
import type { WebhookDeliverySummary, HomeSummary } from '@api';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { useUserContext } from '@hooks/context/useUserContext';
import { useEnvironmentContext } from '@hooks/context/useEnvironmentContext';
import { useCopyToClipboard } from '@hooks/helpers/useCopyToClipboard';
import { prettifyAmount } from '@utils/index';
import CodeBlock from '../_shared/CodeBlock';
import styles from './styles.module.scss';

const DOCS_URL = 'https://docs.xeppt.com';
const DOCS_QUICKSTART_URL = 'https://docs.xeppt.com/getting-started/quickstart';

// Illustrative quickstart sample — the merchant's real access key + values are
// shown elsewhere on this page (API keys card); this is documentation copy,
// not account data, so it's fine to hardcode like every other API sample.
const QUICKSTART_CODE = `curl https://api.xeppt.com/v1/payments \\
  -H "Authorization: Bearer ak_test_51Nk9xxxxxxxxxxxxxxxxxxxx" \\
  -d amount=4200 \\
  -d currency=cad \\
  -d description="Order #1042"
# → 200 OK  { "id": "pay_3Nk1x", "status": "succeeded" }`;

type TQuickLink = {
    label: string;
    description: string;
    icon: TIconType;
    to: string;
};

const quickLinks: TQuickLink[] = [
    {
        label: 'API keys',
        description: 'Create and manage secret keys',
        icon: 'key',
        to: routes.developers_api_keys
    },
    {
        label: 'Webhooks',
        description: 'Subscribe to real-time events',
        icon: 'link',
        to: routes.developers_webhooks
    },
    {
        label: 'Payments',
        description: 'Track payments and refunds',
        icon: 'money',
        to: routes.developers_payments
    },
    {
        label: 'Payouts',
        description: 'Send payouts to your customers',
        icon: 'send',
        to: routes.developers_payouts
    },
    {
        label: 'Gift cards',
        description: 'Issue and manage gift cards',
        icon: 'gift_card',
        to: routes.developers_gift_cards
    },
    {
        label: 'Sandbox',
        description: 'Test without touching live data',
        icon: 'cloud',
        to: routes.developers_sandbox
    }
];

// Every row renders the SAME real `status` signal from HomeSummary — this
// endpoint only ever reports the XEPPT API's own health as one boolean, never
// independent per-service checks. The labels mirror real product surfaces
// (nav items above) so the panel reads like the approved mockup without
// implying monitoring granularity that doesn't exist.
const STATUS_ROWS = ['Payments API', 'Payouts & settlement', 'Webhooks delivery', 'Sandbox'];

// Shows the first 10 + last 4 characters of a real access key, bulleting the
// rest — never a fabricated placeholder, just a masked view of the real value.
function maskAccessKey(key: string): string {
    if (key.length <= 14) return key;
    return `${key.slice(0, 10)}${'•'.repeat(10)}${key.slice(-4)}`;
}

// Compact "Ns/Nm/Nh/Nd ago" for a real delivery timestamp — a presentation of
// real data (WebhookDeliverySummary.created_at), not invented telemetry.
function formatRelativeTime(iso: string): string {
    const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.round(hours / 24)}d ago`;
}

// A delivery id isn't a secret (unlike an access key), so this is a plain
// truncation for row density — never bulleted, never fabricated.
function truncateId(id: string): string {
    if (id.length <= 12) return id;
    return `${id.slice(0, 10)}…`;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
    CAD: '$',
    USD: '$',
    EUR: '€',
    GBP: '£'
};

// Deliberately derives the symbol from the response's real wallet currency
// instead of hardcoding "$" — a merchant's wallet isn't guaranteed to be
// CAD/USD, and an unrecognized code falls back to the code itself rather
// than silently mislabeling the amount.
export function formatMoney(amount: number, currency: string): string {
    const code = currency.toUpperCase();
    const symbol = CURRENCY_SYMBOLS[code] ?? `${code} `;
    return `${symbol}${prettifyAmount(amount.toFixed(2))}`;
}

// null means "no terminal (approved+failed) attempts yet this window" — a
// distinct state from a real 0% success rate, so it renders as an em dash,
// never "NaN%" or a misleading "0.0%". Split into value/unit so callers that
// style the "%" differently from the figure (hero-side, the metrics card)
// don't re-derive this same null-handling themselves.
export function formatSuccessRateParts(rate: number | null): { value: string; unit: string } {
    if (rate === null) return { value: '—', unit: '' };
    return { value: (rate * 100).toFixed(1), unit: '%' };
}

export function formatSuccessRate(rate: number | null): string {
    const { value, unit } = formatSuccessRateParts(rate);
    return `${value}${unit}`;
}

export type DeltaTone = 'up' | 'down' | 'flat';

export interface DeltaDisplay {
    tone: DeltaTone;
    text: string;
}

// Percentage-point change for a 0..1 fraction metric (e.g. success rate).
// Pure subtraction, never a division, so a previous value of exactly 0 is a
// perfectly real baseline — only an ABSENT (null) side means "no real
// baseline yet", which this returns as null rather than fabricating a delta.
export function computePointsChange(
    current: number | null,
    previous: number | null
): number | null {
    if (current === null || previous === null) return null;
    return (current - previous) * 100;
}

// Percentage change relative to a previous period's value. Unlike points
// change this is a ratio, so a previous value of 0 (or an absent baseline)
// can't produce a meaningful percentage — returns null rather than an
// infinite or fabricated swing, so callers fall back to the honest "Last 30
// days" label instead.
export function computePercentChange(
    current: number | null,
    previous: number | null
): number | null {
    if (current === null || previous === null || previous === 0) return null;
    return ((current - previous) / previous) * 100;
}

// A delta that rounds to 0.0 at display precision reads as "flat" — never a
// misleading "+0.0" up/down arrow for a rounding artifact.
function toneForRounded(rounded: number): DeltaTone {
    if (rounded > 0) return 'up';
    if (rounded < 0) return 'down';
    return 'flat';
}

// Renders a real percentage-points delta (success rate vs its previous 30
// days) as "+1.2 pts" / "−0.4 pts" / a muted "~ stable" — or null when
// there's no real baseline, so the caller renders the honest "Last 30 days"
// label instead of a fake delta.
export function formatPointsDelta(
    current: number | null,
    previous: number | null
): DeltaDisplay | null {
    const delta = computePointsChange(current, previous);
    if (delta === null) return null;
    const rounded = Math.round(delta * 10) / 10;
    const tone = toneForRounded(rounded);
    if (tone === 'flat') return { tone, text: '~ stable' };
    const sign = tone === 'up' ? '+' : '−';
    return { tone, text: `${sign}${Math.abs(rounded).toFixed(1)} pts` };
}

// Renders a real period-over-period percentage delta (e.g. volume vs its
// previous 30 days) as "+6.1% vs previous 30 days" / "−3.2% vs previous 30
// days" / a muted "~ stable" — or null when there's no real baseline, so the
// caller renders the honest "Last 30 days" label instead of a fake delta.
export function formatPercentDelta(
    current: number | null,
    previous: number | null
): DeltaDisplay | null {
    const delta = computePercentChange(current, previous);
    if (delta === null) return null;
    const rounded = Math.round(delta * 10) / 10;
    const tone = toneForRounded(rounded);
    if (tone === 'flat') return { tone, text: '~ stable' };
    const sign = tone === 'up' ? '+' : '−';
    return { tone, text: `${sign}${Math.abs(rounded).toFixed(1)}% vs previous 30 days` };
}

// Pure geometry for the hand-rolled sparkline SVG (no chart library). Scales
// `values` into SVG-space points — y grows downward, so the highest value
// maps to y=0. Guards the two degenerate cases a real response can send: an
// all-equal series (e.g. a brand-new merchant's all-zero daily_volume, which
// would otherwise divide by a zero range) and a single-point series (which
// would otherwise divide by a zero step).
export function computeSparklinePoints(
    values: number[],
    width: number,
    height: number
): Array<{ x: number; y: number }> {
    if (values.length === 0) return [];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const stepX = values.length > 1 ? width / (values.length - 1) : 0;
    return values.map((value, index) => ({
        x: index * stepX,
        y: height - ((value - min) / range) * height
    }));
}

const SPARK_WIDTH = 112;
const SPARK_HEIGHT = 40;

// Decorative-only trend line — aria-hidden since the value it illustrates is
// already announced as text by the metric card's own value/period labels.
function Sparkline({ values }: { values: number[] }) {
    const points = computeSparklinePoints(values, SPARK_WIDTH, SPARK_HEIGHT);
    if (points.length === 0) return null;

    const linePath = points
        .map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
        .join(' ');
    const areaPath = `${linePath} L${SPARK_WIDTH} ${SPARK_HEIGHT} L0 ${SPARK_HEIGHT} Z`;
    const last = points[points.length - 1];

    return (
        <svg
            className={styles.met_spark}
            viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
            preserveAspectRatio="none"
            aria-hidden="true">
            <path d={areaPath} className={styles.spark_area} />
            <path d={linePath} className={styles.spark_line} />
            <circle cx={last.x} cy={last.y} r={2.6} className={styles.spark_dot} />
        </svg>
    );
}

// Caps the mini bar-chart's rendered segments so an unusually large retry
// budget can't blow out the row's width — this is a "how much of the retry
// budget did this delivery use" indicator built from the delivery's real
// attempts/max_attempts, never a fabricated trend.
const MAX_ATTEMPT_BARS = 4;

export function computeAttemptBars(attempts: number, maxAttempts: number): boolean[] {
    const total = Math.min(Math.max(maxAttempts, 1), MAX_ATTEMPT_BARS);
    const filled = Math.min(Math.max(attempts, 0), total);
    return Array.from({ length: total }, (_, index) => index < filled);
}

function EventRow({ delivery }: { delivery: WebhookDeliverySummary }) {
    const tone =
        delivery.status === 'delivered' ? 'ok' : delivery.status === 'failed' ? 'failed' : 'pending';
    const barClass =
        tone === 'ok'
            ? styles.event_bar_ok
            : tone === 'failed'
            ? styles.event_bar_failed
            : styles.event_bar_pending;

    return (
        <div className={styles.event_row}>
            <span className={styles.event_type}>{delivery.event_type}</span>
            <span className={styles.event_ref}>{truncateId(delivery.id)}</span>
            <span className={styles.event_bars} aria-hidden="true">
                {computeAttemptBars(delivery.attempts, delivery.max_attempts).map(
                    (filled, index) => (
                        <i
                            key={index}
                            className={[styles.event_bar, filled ? barClass : ''].join(' ')}
                        />
                    )
                )}
            </span>
            <span className={styles.event_meta}>
                {delivery.response_status ?? '—'} · {formatRelativeTime(delivery.created_at)}
            </span>
        </div>
    );
}

// Hero-side stats — real 30-day telemetry restoring the mockup's right-aligned
// two-stat visual, but with honest figures (never the mockup's placeholder
// uptime/latency).
export function HeroStats({ summary }: { summary: HomeSummary }) {
    const successRate = formatSuccessRateParts(summary.last_30_days.success_rate);

    return (
        <>
            <div className={styles.hero_stat}>
                Success rate (30d)
                <span className={styles.hero_stat_value}>
                    {successRate.value}
                    {successRate.unit && (
                        <span className={styles.hero_stat_unit}>{successRate.unit}</span>
                    )}
                </span>
            </div>
            <div className={styles.hero_stat}>
                Processed (30d)
                <span className={styles.hero_stat_value}>
                    {formatMoney(summary.last_30_days.approved_amount, summary.currency)}
                </span>
            </div>
        </>
    );
}

// A metric card's sub-line: the real vs-previous-period delta when one
// exists (up/down arrow, toned green/red, "flat" gets no arrow — same as the
// mockup's `.delta.flat`), or the same honest "Last 30 days" label the card
// would show with no delta at all when there's no real baseline yet (e.g. a
// merchant with under 60 days of history) — never a fabricated 0%/0pts.
function MetricDeltaOrPeriod({ delta }: { delta: DeltaDisplay | null }) {
    if (!delta) {
        return <span className={styles.met_period}>Last 30 days</span>;
    }

    const toneClass =
        delta.tone === 'up'
            ? styles.met_delta_up
            : delta.tone === 'down'
            ? styles.met_delta_down
            : styles.met_delta_flat;

    return (
        <span className={[styles.met_delta, toneClass].join(' ')}>
            {delta.tone !== 'flat' && (
                <Icon
                    name={delta.tone === 'up' ? 'arrow_up' : 'arrow_down'}
                    width={13}
                    height={13}
                />
            )}
            {delta.text}
        </span>
    );
}

// The metrics row's three resolved (non-skeleton) cards — mirrors the
// mockup's `.met` cards (label / big tabular value / sub-line / sparkline),
// in the mockup's own left-to-right order: API requests, success rate,
// processed volume. Success rate has no daily series in the API contract, so
// it renders without a sparkline rather than inventing one. Success rate and
// volume both show a REAL period-over-period delta when `previous_30_days`
// supports one; API requests has no previous-period figure in the contract,
// so it always shows the honest "Last 30 days" label instead of a delta.
export function MetricsCards({ summary }: { summary: HomeSummary }) {
    const successRate = formatSuccessRateParts(summary.last_30_days.success_rate);
    const successRateDelta = formatPointsDelta(
        summary.last_30_days.success_rate,
        summary.previous_30_days?.success_rate ?? null
    );
    const volumeDelta = formatPercentDelta(
        summary.last_30_days.approved_amount,
        summary.previous_30_days?.approved_amount ?? null
    );
    // The api_requests block is optional on the wire (an older backend omits
    // it). When present, a real 0 is an honest figure and IS shown; when
    // absent the count is genuinely UNKNOWN and must degrade to the same
    // em-dash "no data" convention the success rate uses — never a fabricated
    // 0, which would be indistinguishable from a confirmed zero.
    const apiRequests = summary.api_requests;

    return (
        <>
            <div className={styles.met}>
                <span className={styles.met_label}>API requests</span>
                <span className={styles.met_value}>
                    {apiRequests ? prettifyAmount(apiRequests.last_30_days) : '—'}
                </span>
                <span className={styles.met_period}>Last 30 days</span>
                <Sparkline values={(apiRequests?.daily ?? []).map((day) => day.count)} />
            </div>
            <div className={styles.met}>
                <span className={styles.met_label}>Success rate</span>
                <span className={styles.met_value}>
                    {successRate.value}
                    {successRate.unit && (
                        <span className={styles.met_value_unit}>{successRate.unit}</span>
                    )}
                </span>
                <MetricDeltaOrPeriod delta={successRateDelta} />
            </div>
            <div className={styles.met}>
                <span className={styles.met_label}>Processed volume</span>
                <span className={styles.met_value}>
                    {formatMoney(summary.last_30_days.approved_amount, summary.currency)}
                </span>
                <MetricDeltaOrPeriod delta={volumeDelta} />
                <Sparkline values={summary.daily_volume.map((day) => day.approved_amount)} />
            </div>
        </>
    );
}

// Renders all three real states itself (loading / loaded / unavailable) so
// the parent can just drop <StatusCard .../> into the grid. Every row mirrors
// the SAME summary.status signal (see STATUS_ROWS above) — never independent
// per-service checks.
function StatusCard({
    isLoading,
    summary
}: {
    isLoading: boolean;
    summary: HomeSummary | undefined;
}) {
    if (isLoading) {
        return (
            <div className={styles.card_list_pad}>
                <Skeleton className={styles.status_skeleton} />
            </div>
        );
    }

    if (!summary) {
        return (
            <div className={styles.card_list_pad}>
                <Typography variant="body4" className={styles.event_empty}>
                    Status unavailable right now.
                </Typography>
            </div>
        );
    }

    const isOperational = summary.status === 'operational';

    return (
        <>
            <div
                className={[
                    styles.status_head,
                    isOperational ? '' : styles.status_head_degraded
                ].join(' ')}>
                <span
                    className={[
                        styles.status_dot,
                        isOperational ? '' : styles.status_dot_degraded
                    ].join(' ')}
                />
                <Typography
                    variant="body3"
                    weight="bold"
                    className={
                        isOperational ? styles.status_head_text : styles.status_head_text_degraded
                    }>
                    {isOperational
                        ? 'All systems operational'
                        : `Current status: ${summary.status}`}
                </Typography>
            </div>
            <div className={styles.card_list_pad}>
                {STATUS_ROWS.map((label) => (
                    <div key={label} className={styles.status_row}>
                        <Typography variant="body4" className={styles.status_row_name}>
                            {label}
                        </Typography>
                        <span
                            className={[
                                styles.status_row_state,
                                isOperational ? '' : styles.status_row_state_degraded
                            ].join(' ')}>
                            <span
                                className={[
                                    styles.status_row_dot,
                                    isOperational ? '' : styles.status_row_dot_degraded
                                ].join(' ')}
                            />
                            {isOperational ? 'Operational' : 'Degraded'}
                        </span>
                    </div>
                ))}
                <Typography variant="caption1" className={styles.status_footnote}>
                    Reflects the XEPPT API's own health — not third-party processors.
                </Typography>
            </div>
        </>
    );
}

const DevelopersHomeLayout = () => {
    const { user } = useUserContext();
    // The webhook-service calls and the home-summary call below are all
    // scoped to the pill — the API-keys widget on this page is a separate,
    // not-yet-migrated surface, left untouched here.
    const { env } = useEnvironmentContext();
    const navigate = useNavigate();
    const { copied, copy } = useCopyToClipboard();

    const { data: apiKeys, isLoading: isApiKeysLoading } = useApiQuery({
        method: () => apiApiKeysService.getApiKeys(),
        deps: [user],
        condition: !!user
    });

    const { data: webhookData } = useApiQuery({
        method: () => apiWebhookService.listEndpoints(env),
        deps: [user, env],
        condition: !!user
    });

    const { data: deliveryData, isLoading: isDeliveriesLoading } = useApiQuery({
        method: () => apiWebhookService.listDeliveries({ environment: env, limit: 4 }),
        deps: [user, env],
        condition: !!user
    });

    const { data: homeSummary, isLoading: isSummaryLoading } = useApiQuery({
        method: () => apiHomeSummaryService.getSummary(env),
        deps: [user, env],
        condition: !!user
    });

    const accessKey = apiKeys?.access_key;
    const hasApiKey = !!accessKey;
    const isLiveKey = accessKey?.startsWith('ak_live_') ?? false;
    const hasWebhook = (webhookData?.endpoints?.length ?? 0) > 0;
    const deliveries = deliveryData?.deliveries ?? [];

    const setupSteps = useMemo(
        () => [
            {
                key: 'api-key',
                title: 'Create your API key',
                description: 'Authenticate API requests with a secret key.',
                done: hasApiKey,
                actionLabel: hasApiKey ? 'Manage key' : 'Create key',
                onAction: () => navigate(routes.developers_api_keys)
            },
            {
                key: 'webhook',
                title: 'Add a webhook endpoint',
                description: 'Receive real-time event notifications.',
                done: hasWebhook,
                actionLabel: hasWebhook ? 'Manage webhooks' : 'Add webhook',
                onAction: () => navigate(routes.developers_webhooks)
            }
        ],
        [hasApiKey, hasWebhook, navigate]
    );

    const doneCount = setupSteps.filter((step) => step.done).length;
    const allDone = doneCount === setupSteps.length;
    const progressPct = Math.round((doneCount / setupSteps.length) * 100);

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            {/* ── Hero ─────────────────────────────────────────────────── */}
            <section className={styles.hero}>
                <div className={styles.hero_in}>
                    <p className={styles.eyebrow}>Developer platform</p>
                    <h1 className={styles.hero_title}>
                        Move money into <em>anything</em> you build.
                    </h1>
                    <p className={styles.hero_sub}>
                        Accept payments, run payouts, issue gift cards and settle to any bank —
                        one API, sandbox to live in minutes. Built on XEPPT's regulated Canadian
                        payment rails.
                    </p>
                    <div className={styles.hero_cta}>
                        <button
                            type="button"
                            className={styles.btn_gold}
                            onClick={() => navigate(routes.developers_api_keys)}>
                            <Icon name="plus" width={16} height={16} />
                            {hasApiKey ? 'Manage API key' : 'Create API key'}
                        </button>
                        <a
                            className={styles.btn_ghost}
                            href={DOCS_QUICKSTART_URL}
                            target="_blank"
                            rel="noreferrer">
                            <Icon name="external_link" width={16} height={16} />
                            Read the quickstart
                        </a>
                    </div>
                </div>
                <div className={styles.hero_side}>
                    {isSummaryLoading || !homeSummary ? (
                        <>
                            <Skeleton className={styles.hero_stat_skeleton} />
                            <Skeleton className={styles.hero_stat_skeleton} />
                        </>
                    ) : (
                        <HeroStats summary={homeSummary} />
                    )}
                </div>
            </section>

            {/* ── Setup / getting started ─────────────────────────────── */}
            {allDone ? (
                // Once every step is done, de-emphasize this from a full 2-step
                // card down to a single-line success banner — it no longer needs
                // the same visual weight as an in-progress checklist.
                <div className={styles.setup_done}>
                    <span className={styles.setup_done_icon}>
                        <Icon name="filled_check" width={18} height={18} />
                    </span>
                    <Typography variant="body4" className={styles.setup_done_text}>
                        <strong>You're all set up.</strong> Your account is ready to accept
                        live traffic.
                    </Typography>
                </div>
            ) : (
                <section className={styles.setup_card}>
                    <div className={styles.setup_head}>
                        <div className={styles.setup_head_text}>
                            <Typography variant="body1" weight="semibold">
                                Set up your integration
                            </Typography>
                            <Typography variant="body4" className={styles.setup_sub}>
                                Complete these steps to start building with XEPPT.
                            </Typography>
                        </div>
                        <div className={styles.progress_wrap}>
                            <div className={styles.progress_meta}>
                                <span className={styles.progress_count}>
                                    {doneCount} of {setupSteps.length}
                                </span>
                                <span className={styles.progress_label}>complete</span>
                            </div>
                            <div className={styles.progress_track}>
                                <div
                                    className={styles.progress_fill}
                                    style={{ width: `${progressPct}%` }}
                                />
                            </div>
                        </div>
                    </div>

                    {isApiKeysLoading ? (
                        <div className={styles.setup_steps}>
                            <Skeleton className={styles.step_skeleton} />
                            <Skeleton className={styles.step_skeleton} />
                        </div>
                    ) : (
                        <div className={styles.setup_steps}>
                            {setupSteps.map((step, index) => (
                                <div
                                    key={step.key}
                                    className={[
                                        styles.step,
                                        step.done ? styles.step_done : ''
                                    ].join(' ')}>
                                    <div className={styles.step_marker}>
                                        {step.done ? (
                                            <Icon name="filled_check" width={22} height={22} />
                                        ) : (
                                            <span className={styles.step_index}>
                                                {index + 1}
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.step_text}>
                                        <Typography variant="body3" weight="semibold">
                                            {step.title}
                                        </Typography>
                                        <Typography
                                            variant="caption1"
                                            className={styles.step_desc}>
                                            {step.description}
                                        </Typography>
                                    </div>
                                    <Button
                                        variant={step.done ? 'outlined-gold' : 'gold'}
                                        size="small"
                                        onClick={step.onAction}>
                                        {step.actionLabel}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            )}

            {/* ── Quickstart + API keys ───────────────────────────────── */}
            <section className={styles.grid2}>
                <div className={styles.card}>
                    <div className={styles.card_head}>
                        <Typography variant="body1" weight="semibold">
                            Your first charge
                        </Typography>
                        <span className={styles.card_head_sub}>— live in one request</span>
                        <span className={styles.spacer} />
                        <a
                            className={styles.link_gold}
                            href={DOCS_URL}
                            target="_blank"
                            rel="noreferrer">
                            Full reference
                            <Icon name="arrow_right" width={13} height={13} />
                        </a>
                    </div>
                    <div className={styles.card_body}>
                        <CodeBlock language="cURL" code={QUICKSTART_CODE} />
                    </div>
                </div>

                <div className={styles.card}>
                    <div className={styles.card_head}>
                        <Typography variant="body1" weight="semibold">
                            API keys
                        </Typography>
                        <span className={styles.spacer} />
                        <button
                            type="button"
                            className={styles.link_gold}
                            onClick={() => navigate(routes.developers_api_keys)}>
                            Manage
                        </button>
                    </div>
                    <div className={styles.card_list_pad}>
                        {isApiKeysLoading ? (
                            <Skeleton className={styles.key_skeleton} />
                        ) : accessKey ? (
                            <div className={styles.key_row}>
                                <div className={styles.key_meta}>
                                    <span className={styles.key_type}>
                                        Access key
                                        <span
                                            className={[
                                                styles.pill,
                                                isLiveKey ? styles.pill_live : styles.pill_test
                                            ].join(' ')}>
                                            {isLiveKey ? 'Live' : 'Sandbox'}
                                        </span>
                                    </span>
                                    <span className={styles.key_val}>
                                        {maskAccessKey(accessKey)}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    className={styles.key_copy}
                                    onClick={() => void copy(accessKey)}
                                    aria-label="Copy access key">
                                    <Icon
                                        name={copied ? 'filled_check' : 'copy'}
                                        width={15}
                                        height={15}
                                    />
                                </button>
                            </div>
                        ) : (
                            <div className={styles.key_empty}>
                                <Typography variant="body4" className={styles.key_empty_text}>
                                    No API key yet — create one to start making requests.
                                </Typography>
                                <Button
                                    variant="gold"
                                    size="small"
                                    onClick={() => navigate(routes.developers_api_keys)}>
                                    Create key
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* ── Metrics — real telemetry, last 30 days ──────────────── */}
            {(isSummaryLoading || homeSummary) && (
                <section className={styles.metrics}>
                    {isSummaryLoading || !homeSummary ? (
                        <>
                            <Skeleton className={styles.met_skeleton} />
                            <Skeleton className={styles.met_skeleton} />
                            <Skeleton className={styles.met_skeleton} />
                        </>
                    ) : (
                        <MetricsCards summary={homeSummary} />
                    )}
                </section>
            )}

            {/* ── Recent events + status — real webhook deliveries and the
                 XEPPT API's real health signal, side by side like the
                 approved mockup ───────────────────────────────────────── */}
            <section className={styles.grid2}>
                <div className={styles.card}>
                    <div className={styles.card_head}>
                        <Typography variant="body1" weight="semibold">
                            Recent events
                        </Typography>
                        <span className={styles.spacer} />
                        <button
                            type="button"
                            className={styles.link_gold}
                            onClick={() => navigate(routes.developers_webhooks)}>
                            View all
                        </button>
                    </div>
                    <div className={styles.card_list_pad}>
                        {isDeliveriesLoading ? (
                            <Skeleton className={styles.event_skeleton} />
                        ) : deliveries.length > 0 ? (
                            deliveries.map((delivery) => (
                                <EventRow key={delivery.id} delivery={delivery} />
                            ))
                        ) : (
                            <Typography variant="body4" className={styles.event_empty}>
                                No events yet — deliveries will show up here once requests
                                start flowing.
                            </Typography>
                        )}
                    </div>
                </div>

                <div className={styles.card}>
                    <StatusCard isLoading={isSummaryLoading} summary={homeSummary} />
                </div>
            </section>

            {/* ── Quick links ─────────────────────────────────────────── */}
            <section className={styles.section}>
                <Typography variant="h5" weight="semibold">
                    Explore the platform
                </Typography>
                <div className={styles.tiles}>
                    {quickLinks.map((link) => (
                        <button
                            key={link.to}
                            className={styles.tile}
                            onClick={() => navigate(link.to)}>
                            <span className={styles.tile_icon}>
                                <Icon name={link.icon} width={20} height={20} />
                            </span>
                            <span className={styles.tile_body}>
                                <Typography variant="body3" weight="semibold">
                                    {link.label}
                                </Typography>
                                <Typography variant="caption1" className={styles.tile_desc}>
                                    {link.description}
                                </Typography>
                            </span>
                            <Icon
                                name="nav_right"
                                width={16}
                                height={16}
                                className={styles.tile_arrow}
                            />
                        </button>
                    ))}
                </div>
            </section>

            {/* ── Resources ───────────────────────────────────────────── */}
            <section className={styles.section}>
                <Typography variant="h5" weight="semibold">
                    Resources
                </Typography>
                <div className={styles.resources}>
                    <a
                        className={styles.resource}
                        href={DOCS_URL}
                        target="_blank"
                        rel="noreferrer">
                        <span className={styles.resource_icon}>
                            <Icon name="document" width={18} height={18} />
                        </span>
                        <span className={styles.resource_text}>
                            <Typography variant="body4" weight="semibold">
                                API reference
                            </Typography>
                            <Typography variant="caption1" className={styles.resource_desc}>
                                Full REST API documentation
                            </Typography>
                        </span>
                        <Icon name="external_link" width={14} height={14} />
                    </a>
                    <a
                        className={styles.resource}
                        href={DOCS_QUICKSTART_URL}
                        target="_blank"
                        rel="noreferrer">
                        <span className={styles.resource_icon}>
                            <Icon name="bulb" width={18} height={18} />
                        </span>
                        <span className={styles.resource_text}>
                            <Typography variant="body4" weight="semibold">
                                Quickstart guide
                            </Typography>
                            <Typography variant="caption1" className={styles.resource_desc}>
                                Your first request in minutes
                            </Typography>
                        </span>
                        <Icon name="external_link" width={14} height={14} />
                    </a>
                    <button
                        className={styles.resource}
                        onClick={() => navigate(routes.help_center)}>
                        <span className={styles.resource_icon}>
                            <Icon name="headset" width={18} height={18} />
                        </span>
                        <span className={styles.resource_text}>
                            <Typography variant="body4" weight="semibold">
                                Support
                            </Typography>
                            <Typography variant="caption1" className={styles.resource_desc}>
                                Get help from our team
                            </Typography>
                        </span>
                        <Icon name="nav_right" width={14} height={14} />
                    </button>
                </div>
            </section>
        </motion.div>
    );
};

export default DevelopersHomeLayout;
